import React, {useEffect, useLayoutEffect, useMemo, useState} from "react";
import {Col} from "reactstrap";
import Api from "../../../../../../../core/services/api/api";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import AddProductImageDropZone from "../../../../../../components/app-specific/dropzone/add-product-image";
import {MarkProductForCacheSectionNames, UploadStates} from "../../../../../../../core/constants/enums";
import {deepCopy, deepEqual, numComparator} from "../../../../../../../core/services/utils/utils";
import Utils from "../../../../../../../core/services/utils/utils";
import EditImageDialog from "../../../../../../components/dialogs/edit-image";
import {ReactSortable} from "react-sortablejs";
import ProductImageCard from "../../../../../../components/app-specific/product-cards/product-image-card";
import LazyImage from "../../../../../../components/app-specific/lazy-image";
import classnames from "classnames";
import {useParams} from "react-router-dom";

const initEditImage = {open: false, file: null, url: null, imageId: null};

const ProductInformationImagesView = ({loading: loadingProps, setIsCacheNeeded}) => {
    const params = useParams()
    const [loadingImages, setLoadingImages] = useState(true);
    const [reordering, setReordering] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [images, _setImages] = useState([]);
    const [selected, setSelected] = useState(null);
    const [imageToReorder, setImageToReorder] = useState(null);
    const isMounted = useIsMounted();

    const [editImage, setEditImage] = useState(initEditImage);
    const [adding, setAdding] = useState(false);
    const [uploadState, setUploadState] = useState({});
    const [tempUrl, setTempUrl] = useState();

    const loading = useMemo(() => loadingImages || loadingProps, [loadingProps, loadingImages])
    const reorderable = useMemo(() => !removing && !adding && !reordering, [removing, adding, reordering]);

    /**
     * With each change in id of the url parameters:
     * - fetches the images of this product from the server.
     */
    useEffect(() => {
        getProductImages().then();
    }, [params?.id])

    /**
     * With each change in images:
     * - if the selected image is no longer in images list, removes it, otherwise updates it
     * - if image to reorder is no longer in images list, removes it, otherwise updates it
     */
    useLayoutEffect(() => {
        if (selected) {
            setSelected(images.find(e => e.id === selected.id) ?? null);
        }
        if (imageToReorder) {
            setImageToReorder(images.find(e => e.id === imageToReorder.id) ?? null)
        }
    }, [images])

    /**
     * Sets the images state of this component by sorting them based on their orderIndex after the new state is
     * received.
     *
     * This method acts as a wrapper that injects the sorting behaviour at the end of the setting this state.
     * @param {any[] | function(any[]): any[]} stateOrCallback
     * @param {boolean} sort whether to sort the images or not. used when rearranging the categories in sortablejs
     * @param {function(newState: any[]): void | null} getNewState callback used to get the new state of the images
     * before submission.
     */
    const setImages = (stateOrCallback, sort = true, getNewState = null) => {
        _setImages((prevState) => {
            let newState;
            if (typeof stateOrCallback === 'function') {
                newState = stateOrCallback(prevState)
            } else {
                newState = deepCopy(stateOrCallback)
            }
            if (sort) {
                newState = newState
                        ?.sort((a, b) => {
                            if (typeof a.orderIndex !== 'number' && typeof b.orderIndex !== 'number') return 0;
                            if (typeof a.orderIndex !== 'number') return 1;
                            if (typeof b.orderIndex !== 'number') return -1;
                            return numComparator(a.orderIndex, b.orderIndex);
                        })
                        ?.map((e, index) => ({...e, orderIndex: index + 1}))
                    ?? []
            }
            if (getNewState) {
                getNewState(newState);
            }
            return newState
        })
    }

    /**
     * Fetches the images of the current product from the server.
     * @return {Promise<void>}
     */
    const getProductImages = async () => {
        setLoadingImages(true);
        const response = await Api.getOperatorPartImages(params.id);
        if (!isMounted())
            return;
        setLoadingImages(false);
        if (response?.resultFlag) {
            setImages(response?.data ?? []);
            setSelected(getDefaultSelectedImage(response?.data));
        }
    }

    /**
     * Uploads the selected content as a new image for the current product
     *
     * @param buffer
     * @param dataUrl
     * @param name
     * @return {Promise<void>}
     */
    const addProductImage = async ({buffer, dataUrl, name}) => {
        setTempUrl(dataUrl);
        setAdding(true);
        const forApi = {
            partId: parseInt(params.id),
            isCover: !images?.length,
            fileName: Utils.sanitizeFileName(name),
            content: btoa(buffer)
        }
        const response = await Api.createOperatorPartImage(forApi, onUploadProgress);
        if (!isMounted()) return;
        setAdding(false);
        if (!response?.resultFlag) {
            return setUploadState({
                state: UploadStates.error,
                percentage: 0,
            })
        }
        setTempUrl(null);
        setUploadState({
            state: UploadStates.done,
            percentage: 100,
        })

        Api.markPartsForCache({partIds: [Number(params.id)], sectionNames: [MarkProductForCacheSectionNames.image]})
            .then(response => {
                if (response?.resultFlag) {
                    setIsCacheNeeded?.(true)
                }
            });
        setImages(
            prevState => [...prevState, response.data],
            true,
            (newImages) => {
                if (!selected) {
                    setSelected(getDefaultSelectedImage(newImages));
                }
            }
        );
    }


    /**
     * Uploads the updated content of the selected image for the current product
     *
     * @param buffer
     * @param dataUrl
     * @param name
     * @param imageId
     * @return {Promise<void>}
     */
    const updateProductImage = async ({buffer, dataUrl, name}, imageId) => {
        setTempUrl(dataUrl);
        const forApi = {
            partId: parseInt(params.id),
            imageId: imageId,
            fileName: Utils.sanitizeFileName(name),
            content: btoa(buffer)
        }
        // TODO: call api for updating the image of a product.
        const response = {
            resultFlag: false,
        };
        if (!isMounted())
            return;
        if (response?.resultFlag) {

            Api.markPartsForCache({partIds: [Number(params.id)], sectionNames: [MarkProductForCacheSectionNames.image]})
                .then(response => {
                    if (response?.resultFlag) {
                        setIsCacheNeeded?.(true)
                    }
                });
            setImages(
                prevState => prevState.map(e => e.id === imageId ? response.data : e),
                true,
                (newImages) => {
                    if (!selected) {
                        setSelected(getDefaultSelectedImage(newImages));
                    }
                }
            );
        }

    }

    /**
     * Removes the given image from the current product.
     *
     * * removes the selected image if it's the same
     * * removes the image to reorder if it's the same
     * @param image
     * @return {Promise<ApiResponseDs<*|null>|undefined>}
     */
    const removeProductImage = async (image) => {
        setRemoving(true);
        const response = await Api.removeOperatorPartImage(image.id);
        if (!isMounted()) return;
        setRemoving(false);
        if (response?.resultFlag) {

            Api.markPartsForCache({partIds: [Number(params.id)], sectionNames: [MarkProductForCacheSectionNames.image]})
                .then(response => {
                    if (response?.resultFlag) {
                        setIsCacheNeeded?.(true)
                    }
                });
            setImages(
                prevState => prevState.filter(e => e.id !== image.id),
                true,
                (newImages) => {
                    if (image.isCover && newImages.length && !newImages.find(e => e.isCover)) {
                        // if the cover image is removed, then choose the first remaining image as the cover image.
                        setAsCoverImage(newImages[0]);
                    } else if (selected?.id === image?.id) {
                        setSelected(getDefaultSelectedImage(newImages))
                    }
                });
        }
        return response;
    }

    // /**
    //  * Toggles the visibility of the given image for the current product.
    //  *
    //  * @param {any} image
    //  * @return {Promise<{resultFlag: boolean} | undefined>}
    //  */
    // const toggleImageVisibility = async (image) => {
    //  Needs api call
    //     const response = {
    //         resultFlag: true,
    //     }
    //     if (!isMounted()) return;
    //     if (response?.resultFlag) {
    //         setImages(prevState => prevState.map(e => e.id === image.id
    //             ? {...e, isVisible: !e.isVisible}
    //             : e
    //         ));
    //     }
    //     return response;
    // }

    /**
     * Sets the given image as the current cover image of the product.
     *
     * @param {any} image
     * @return {Promise<{resultFlag: boolean} | undefined>}
     */
    const setAsCoverImage = async (image) => {
        const response = await Api.setOperatorPartImageAsDefault(image.id);
        if (!isMounted()) return;
        if (response?.resultFlag) {

            Api.markPartsForCache({partIds: [Number(params.id)], sectionNames: [MarkProductForCacheSectionNames.image]})
                .then(response => {
                    if (response?.resultFlag) {
                        setIsCacheNeeded?.(true)
                    }
                });
            setImages(
                prevState => prevState.map(e => e.id === image.id
                    ? {...e, isCover: true}
                    : {...e, isCover: false}
                ),
                true,
                (newImages) => {
                    setSelected(getDefaultSelectedImage(newImages))
                });
        }
        return response;
    }


    /**
     * Fires when any image has been reordered.
     * @param {any[]} newImages
     */
    const reorderImages = async (newImages) => {
        if (deepEqual(newImages.map(e => e.id), images.map(e => e.id))) {
            // if not changed, do nothing.
            return;
        }
        const imagesCopy = deepCopy(images);
        setImages(newImages?.map(e => ({...e, orderIndex: undefined})));
        const forApi = {
            partId: parseInt(params.id),
            imageIdsOrder: newImages?.map(e => e.id),
        }
        setReordering(true);
        const response = await Api.updateOperatorPartImagesOrder(forApi);
        if (!isMounted())
            return;
        setReordering(false);
        if (!response?.resultFlag) {
            setImages(imagesCopy); // revert the change if not successful
        } else {

            Api.markPartsForCache({partIds: [Number(params.id)], sectionNames: [MarkProductForCacheSectionNames.image]})
                .then(response => {
                    if (response?.resultFlag) {
                        setIsCacheNeeded?.(true)
                    }
                });
        }
        return response;
    }

    /**
     * Opens the edit image dialog so that the user can edit the selected file to be uploaded
     * @param files {File[] | null}
     */
    const onFileSelected = async (files) => {
        const data = files[0];
        setEditImage({file: data, open: true});
    }

    /**
     * Tracks the progress of the uploading image, and updates the status based on it.
     * @param progressEvent {ProgressEvent}
     */
    const onUploadProgress = (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadState({
            state: UploadStates.pending,
            percentage: percentCompleted
        });
    }

    /**
     * Sets hovered image as the currently selected image.
     * @param image
     */
    const onImageSelected = (image) => {
        setSelected(image);
    }

    /**
     * Places the reordered image in its appropriate position in the list of images, and calls reorderImages to
     * update the orders of all images in the backend.
     *
     * @param {any} image
     */
    const onImageReordered = async (image) => {
        const newImages = images
            .map((e) => image.id === e.id
                ? image
                : image.orderIndex === e.orderIndex
                    ? {...e, orderIndex: images.find(e => image.id === e.id)?.orderIndex}
                    : e,
            ).sort((a, b) => numComparator(a.orderIndex, b.orderIndex))
        return await reorderImages(newImages);
    }

    /**
     * Fetches the cover image if exists, otherwise fetches the first image if the list is not empty, otherwise
     * undefined.
     *
     * @param images
     * @return {*|undefined}
     */
    const getDefaultSelectedImage = (images) => {
        return images?.find(e => e.isCover) ?? (images?.length ? images[0] : undefined)
    }

    /**
     * Either adds or updates a product image based on the value of [imageId] in the editImage state.
     *
     * @param buffer
     * @param dataUrl
     * @param name
     */
    const onEditingImageFinished = async ({buffer, dataUrl, name}) => {
        if (!editImage.imageId)
            await addProductImage({buffer, dataUrl, name});
        else
            await updateProductImage({buffer, dataUrl, name}, editImage.imageId)
    }

    return (
        <>
            <div className={'product-images-view part-info-paper'}>
                <div className={'d-flex justify-content-end my-3 mr-2'}>
                </div>
                <div className={'d-flex flex-wrap'}>
                    {
                        loading
                            ? <>
                                <Col xs={12} lg={6} className={'order-1 order-lg-1'}>
                                    <div className={'loading product-image-preview'}>
                                        <div/>
                                    </div>
                                </Col>
                                <Col xs={12} lg={6} className={'order-2 order-lg-4 mt-4 images-container'}>
                                    {
                                        Array(4).fill(null).map((_, e) => (
                                            <Col key={e} xs={12} md={4} lg={6}
                                                 className={'mt-4 loading part-images w-100'}>
                                                <div className={'w-100'}/>
                                            </Col>
                                        ))
                                    }
                                </Col>
                                <Col xs={12} lg={6} className={'order-3 order-lg-2'}>
                                    <div className={'loading product-image-preview'}>
                                        <div/>
                                    </div>
                                </Col>
                            </>
                            : <>
                                {
                                    !!images?.length &&
                                    <>
                                        <Col xs={12} lg={6} className={'order-1 order-lg-1'}>
                                            <div className={classnames('preview', {'cover': selected?.isCover})}>
                                                <LazyImage
                                                    src={selected?.url}
                                                    alt={selected?.fileName}
                                                    onContextMenu={(e) => e.preventDefault()}
                                                    onDragStart={(e) => e.preventDefault()}
                                                    className={'disable-downloading-image'}
                                                />
                                            </div>
                                        </Col>
                                        <Col xs={12} lg={6} className={'order-2 order-lg-3 mt-4'}>
                                            <p className={'text-color-2 font-weight-semi-bold text-sm'}>
                                                Click on the Order Number or Drag and Drop in Order to Define the
                                                Order of the Images
                                            </p>
                                            <ReactSortable
                                                sort
                                                className={'images-container'}
                                                list={images ?? []}
                                                setList={reorderImages}
                                                handle={'.handle'}
                                                group={'images'}
                                                animation={210}
                                                forceFallback={false}
                                                swapThreshold={0.65}
                                                ghostClass={"dragged-image"}
                                            >
                                                {
                                                    images?.map(image => (
                                                        <Col key={image.id} xs={12} md={4} lg={6}
                                                             className={'px-0 mt-4'}
                                                        >
                                                            <ProductImageCard
                                                                image={image}
                                                                remove={removeProductImage}
                                                                setAsCover={setAsCoverImage}
                                                                // toggleVisibility={toggleImageVisibility}
                                                                onReordered={onImageReordered}
                                                                onSelect={onImageSelected}
                                                                onEdit={(image) => setEditImage({
                                                                    url: image.url,
                                                                    imageId: image.id,
                                                                    open: true
                                                                })}
                                                                sortable={reorderable}
                                                                selected={selected?.id === image.id}
                                                                imagesLength={images?.length ?? 0}
                                                            />
                                                        </Col>
                                                    ))
                                                }
                                            </ReactSortable>
                                        </Col>
                                    </>
                                }
                                <Col xs={12} lg={!images?.length ? 12 : 6} className={'order-3 order-lg-2 transition'}>
                                    <AddProductImageDropZone
                                        disabled={adding}
                                        uploadState={uploadState}
                                        uploading={adding}
                                        tempUrl={tempUrl}
                                        setTempUrl={setTempUrl}
                                        onSuccess={onFileSelected}
                                    />
                                </Col>
                            </>
                    }
                </div>
            </div>
            <EditImageDialog
                onlySubmitWhenDone
                file={editImage.file}
                url={editImage.url}
                open={editImage.open}
                edit={!!editImage.url}
                setOpen={() => setEditImage(initEditImage)}
                onFinished={onEditingImageFinished}
            />
        </>
    )
}

export default ProductInformationImagesView;
